import type { FocusEvent, ForwardedRef, Ref } from 'react'
import { useRef, forwardRef } from 'react'
import tw, { styled } from 'twin.macro'

import type { SearchInputProps } from './types'
import { useControllableState, useForkRef } from '../../hooks'
import type { ButtonProps } from '../Button/types'
import { Clear } from '../Icons'
import Input from '../Input/Input'
import { useSafeState } from '../../hooks/useSafeState'

export const ClearButtonWrapper = styled.button`
  ${tw`
    relative
    self-stretch
    focus:outline-none
    focus:ring-0
  `}

  &::before {
    content: '';
    ${tw`
      block
      absolute
      px-4
      top-1/2
      left-1/2
      h-full
      transform
      translate-x--1/2
      translate-y--1/2
    `}
  }
`

export const ClearButton = forwardRef(function ClearButton(
  props: ButtonProps,
  ref: Ref<HTMLButtonElement>,
) {
  return (
    <ClearButtonWrapper ref={ref} {...props} aria-label="Clear">
      <Clear tw="h-4 w-4 text-default-muted" />
    </ClearButtonWrapper>
  )
})

function SearchInput(
  {
    value: valueProp,
    defaultValue: defaultValueProp,
    onSearchTermChange,
    onBlur,
    onFocus,
    onClear,
    ...rest
  }: SearchInputProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const clearButtonRef = useRef(null)
  const inputRef = useRef<HTMLInputElement>()
  const forkedRef = useForkRef(ref, inputRef)

  const [value = '', setValue] = useControllableState<string>({
    prop: valueProp,
    defaultProp: defaultValueProp,
    onChange: onSearchTermChange,
  })

  const [isFocused, setIsFocused] = useSafeState(false)

  const handleClear = () => {
    if (onClear) {
      onClear()
    } else {
      setValue('')
    }

    inputRef.current?.focus()
  }

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    const isInputFocused = e.type === 'focus'

    const isClearButtonFocused =
      e.type === 'blur' &&
      e.relatedTarget != null &&
      e.relatedTarget === clearButtonRef.current

    const nextIsFocused = isInputFocused || isClearButtonFocused

    if (nextIsFocused === isFocused) return

    setIsFocused(nextIsFocused)

    if (isInputFocused || isClearButtonFocused) {
      onFocus?.(e)
    } else {
      onBlur?.(e)
    }
  }

  const showClearButton = isFocused && value.length > 0

  return (
    <Input
      ref={forkedRef}
      type="search"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onFocus={handleFocus}
      onBlur={handleFocus}
      endSlot={
        showClearButton && (
          <ClearButton
            ref={clearButtonRef}
            tabIndex={-1}
            onClick={handleClear}
          />
        )
      }
      {...rest}
    />
  )
}

export default forwardRef(SearchInput)
